import React, { useState } from 'react';
import {
    Autocomplete,
    Button,
    Checkbox,
    Dialog,
    TextField,
} from '@biq/apex-ui';
import {
    DialogActions,
    DialogContent,
    Grid,
    InputAdornment,
    ListItem,
    Typography,
} from '@mui/material';
import {
    addGroupMembersModalStyles,
    AddGroupMembersProps,
    GroupMembersData,
} from './create-or-edit-user-group-helper';
import { AddGroupMembersIcon } from '../../../../assets';
import { walletsAndGroupsCommonStyles } from 'pages/wallets-and-groups/wallets-and-groups-helper';
import SearchIcon from '@mui/icons-material/Search';
import { autoCompleteStyles, validationsMessages } from 'lib';

export const AddGroupMembers = ({
    openAddGroupMemberModal,
    handleAddGroupMembersModal,
    groupMembersData,
    existingGroupMembers,
    onAddNewGroupMembers,
}: AddGroupMembersProps): React.JSX.Element => {
    const [addNewGroupMembersData, setAddNewGroupMembersData] = useState<
        GroupMembersData[]
    >([]);
    const [fieldError, setFieldError] = useState<boolean>(false);

    const getOptionLabel = (option: any): any => {
        const foundOption = groupMembersData.find(
            (opt: any) => opt?.id === option.id
        );
        return foundOption ? foundOption.value : '';
    };

    const handleAutoCompleteFieldOnChange = (
        values: GroupMembersData[]
    ): void => {
        setFieldError(false);
        setAddNewGroupMembersData(values);
    };

    const onAddButtonClick = (): void => {
        if (addNewGroupMembersData.length === 0) {
            setFieldError(true);
        } else {
            setFieldError(false);
            onAddNewGroupMembers(addNewGroupMembersData);
            handleAddGroupMembersModal();
        }
    };

    const onCancelButtonClick = (): void => {
        setFieldError(false);
        handleAddGroupMembersModal();
        setAddNewGroupMembersData([]);
    };

    return (
        <Dialog
            closable={true}
            open={openAddGroupMemberModal}
            onClose={handleAddGroupMembersModal}
            title={'Add Group Members'}
            sx={addGroupMembersModalStyles.dialog}
        >
            <DialogContent>
                <Grid sx={addGroupMembersModalStyles.container}>
                    <Grid sx={addGroupMembersModalStyles.imageContainer}>
                        <img
                            src={AddGroupMembersIcon}
                            alt='add-group-members-icon'
                        />
                    </Grid>
                    <Grid>
                        <Autocomplete
                            options={groupMembersData}
                            sx={autoCompleteStyles}
                            disableCloseOnSelect
                            getOptionLabel={getOptionLabel}
                            noOptionsText={
                                'No match found. Try searching by name or email address.'
                            }
                            limitTags={4}
                            onChange={(__, newValue): any => {
                                handleAutoCompleteFieldOnChange(newValue);
                            }}
                            renderOption={(
                                props,
                                option,
                                { selected }
                            ): any => {
                                let isExisting = false;
                                if (existingGroupMembers.length) {
                                    isExisting = existingGroupMembers.some(
                                        (existingMember) =>
                                            existingMember.id === option.id
                                    );
                                }
                                return (
                                    <ListItem
                                        {...props}
                                        aria-disabled={isExisting}
                                    >
                                        <Grid
                                            display={'flex'}
                                            flexDirection={'row'}
                                        >
                                            <Checkbox
                                                checked={selected || isExisting}
                                            />
                                            <Grid
                                                display={'flex'}
                                                flexDirection={'column'}
                                            >
                                                <Typography
                                                    sx={
                                                        walletsAndGroupsCommonStyles.checkboxHeaderStyles
                                                    }
                                                >
                                                    {option.header}
                                                </Typography>
                                                <Typography
                                                    sx={
                                                        walletsAndGroupsCommonStyles.checkboxSubtitleStyles
                                                    }
                                                >
                                                    {option.subtitle}
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                    </ListItem>
                                );
                            }}
                            multiple={true}
                            renderInput={(params): React.JSX.Element => (
                                <TextField
                                    {...params}
                                    size='medium'
                                    label={'Group Members'}
                                    variant='outlined'
                                    required
                                    error={fieldError}
                                    errorText={
                                        fieldError
                                            ? validationsMessages.required
                                            : ''
                                    }
                                    InputProps={{
                                        ...params.InputProps,
                                        startAdornment: [
                                            <InputAdornment
                                                key={'searchIcon'}
                                                position='start'
                                            >
                                                <SearchIcon />
                                            </InputAdornment>,
                                            params.InputProps.startAdornment
                                                ? params.InputProps
                                                      .startAdornment
                                                : [],
                                        ],
                                    }}
                                />
                            )}
                            ListboxProps={{
                                sx: addGroupMembersModalStyles.listboxStyles,
                            }}
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button
                    size='small'
                    variant='text'
                    onClick={onCancelButtonClick}
                >
                    Cancel
                </Button>
                <Button size='small' onClick={onAddButtonClick}>
                    Add
                </Button>
            </DialogActions>
        </Dialog>
    );
};
